import { ApiOperationItems } from "~enums/apiOperationItems";
import { useApiOperations } from "~hooks/useApiOperations.ts";
import { useExportSeasonPools } from "~pages/SeasonPools/SeasonPoolListPage/hooks/useExportSeasonPools.ts";
import { useImportSeasonPools } from "~pages/SeasonPools/SeasonPoolListPage/hooks/useImportSeasonPools.ts";
import type { CreateSeasonPoolModel, UpdateSeasonPoolModel } from "~pages/SeasonPools/types/seasonPoolModel.ts";
import type { DefaultOperationSuccessResponse } from "~types/response.ts";
import { ensureDefaultOperationResponse, isDefaultOperationSucceeded } from "~utils/defaultOperationResponse.ts";
import { useCreateSeasonPool } from "./useCreateSeasonPool.ts";
import { useDeleteSeasonPool } from "./useDeleteSeasonPool.ts";
import { useUpdateSeasonPool } from "./useUpdateSeasonPool.ts";

export const useSeasonPoolService = () => {
  const { mutateAsync: createSeasonPoolFn, isPending: isCreatePending } = useCreateSeasonPool();
  const { mutateAsync: updateSeasonPoolFn, isPending: isUpdatePending } = useUpdateSeasonPool();
  const { mutateAsync: deleteSeasonPoolFn, isPending: isDeletePending } = useDeleteSeasonPool();
  const { mutateAsync: importSeasonPoolsFn, isPending: isImportPending } = useImportSeasonPools();
  const { mutateAsync: exportSeasonPoolsFn, isPending: isExportPending } = useExportSeasonPools();

  const { create, update, remove, execute } = useApiOperations(ApiOperationItems.SEASON_POOL);

  const createSeasonPool = (payload: CreateSeasonPoolModel) =>
    create(() => createSeasonPoolFn(payload), {
      successMessageId: "pool.add.success",
    });

  const updateSeasonPool = (poolCode: string, payload: Omit<UpdateSeasonPoolModel, "poolCode">) =>
    update(() => updateSeasonPoolFn({ ...payload, poolCode }), {
      successMessageId: "pool.update.success",
    });

  const deleteSeasonPool = async (poolCode: string) =>
    Boolean(
      await remove(() => deleteSeasonPoolFn(poolCode), {
        successMessageId: "pool.delete.success",
      }),
    );

  const importSeasonPools = async (fileName: string) =>
    ensureDefaultOperationResponse(
      await execute(() => importSeasonPoolsFn(fileName), {
        successMessageId: "seasonPools.import.started",
        isSucceeded: isDefaultOperationSucceeded,
      }),
      "Season pools import was not started",
    );

  const exportSeasonPools = async (fieldNames: Array<string>): Promise<DefaultOperationSuccessResponse> =>
    ensureDefaultOperationResponse(
      await execute(() => exportSeasonPoolsFn(fieldNames), {
        successMessageId: "seasonPools.export.started",
        isSucceeded: isDefaultOperationSucceeded,
      }),
      "Season pools export was not started",
    );

  return {
    createSeasonPool,
    isCreatePending,
    updateSeasonPool,
    isUpdatePending,
    deleteSeasonPool,
    isDeletePending,
    importSeasonPools,
    isImportPending,
    exportSeasonPools,
    isExportPending,
  };
};
